// Signed-in-but-has-no-team-org landing. Per MODEL.md, workspace is
// an org-level feature — every team-org user gets full collab access,
// and monetization runs through server-side credit metering (PRICING.md),
// not access gates. A user with no team-org has nothing to scope to:
// the workspace_id claim is empty, RLS returns zero rows on every
// tenant table, and the workspace has nothing to render.
//
// This overlay provides an inline "create organization" flow — POSTs
// to `/organizations` using the Symbols bridge access token, then
// refreshes JWT claims so the new org appears in the user's
// workspaces/orgs and the overlay self-dismisses. Toggled by
// USE_ONBOARDING_GATE.
//
// Slug UX (TASK#4407):
//   - Slug field auto-derives from name as user types.
//   - Debounced availability check (GET /organizations?slug=X, 350 ms delay).
//   - On slug_taken the field auto-advances to the first available -2/-3/-N
//     suffix (up to 20 attempts) without user intervention.
//   - If POST returns 409 slug_taken (race condition after passing the live
//     check), modal shows an inline error AND a "Use <slug-N> instead" button
//     — never silently swallows the error.

import { getSymbolsToken, refreshClaims } from '../../shared/governanceBridge.js'
import { getSDK } from '../../shared/functions/sdk.js'
import { USE_ONBOARDING_GATE } from '../functions/featureFlags.js'

// Module-level debounce timer for slug availability checks.
let _slugDebounceTimer = null

const _hasTeamOrg = (root) => {
  const orgs = root?.jwtClaims?.orgs
  if (!Array.isArray(orgs) || orgs.length === 0) return false
  return orgs.some((o) => o?.type == null || o.type === 'team')
}

// Slugify a name — lowercase, hyphenate whitespace, strip non-URL chars.
// Mirrors server-side slugifyName in OrganizationController.js.
const _slugify = (raw) =>
  String(raw || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

// Check whether a slug is available (not taken by a non-deleted org).
// Returns true = available, false = taken, null = check failed (network).
// Replaces the raw fetch('/organizations?slug=X') call site — the SDK
// method threads the auth token + base URL through TokenManager.
const _checkSlugAvailable = async (slug) => {
  if (!slug) return null
  try {
    return await getSDK().checkOrganizationSlug(slug)
  } catch {
    return null
  }
}

// Find the first available slug: tries base, then base-2, base-3, …, base-N.
// Returns the first available candidate, or null if all attempts fail.
const _findAvailableSlug = async (base, maxAttempts = 20) => {
  for (let n = 0; n < maxAttempts; n++) {
    const candidate = n === 0 ? base : `${base}-${n + 1}`
    const available = await _checkSlugAvailable(candidate)
    if (available === true) return candidate
  }
  return null
}

// Schedule a debounced slug availability check (350 ms). Updates root state
// with _orgCreateSlug (the resolved candidate), _orgCreateSlugChecking, and
// _orgCreateSlugStatus ('available' | 'taken' | 'error').
const _scheduleSlugCheck = (rawName, root) => {
  if (_slugDebounceTimer) clearTimeout(_slugDebounceTimer)
  const base = _slugify(rawName)
  if (!base) {
    root.update({
      _orgCreateSlug: '',
      _orgCreateSlugChecking: false,
      _orgCreateSlugStatus: null,
      _orgCreateSlugSuggestion: null
    })
    return
  }
  root.update({ _orgCreateSlug: base, _orgCreateSlugChecking: true, _orgCreateSlugStatus: null })
  _slugDebounceTimer = setTimeout(async () => {
    _slugDebounceTimer = null
    const available = await _checkSlugAvailable(base)
    if (available === true) {
      root.update({
        _orgCreateSlug: base,
        _orgCreateSlugChecking: false,
        _orgCreateSlugStatus: 'available',
        _orgCreateSlugSuggestion: null
      })
    } else if (available === false) {
      // Slug is taken — find the next free suffix automatically.
      const suggestion = await _findAvailableSlug(base)
      root.update({
        _orgCreateSlug: suggestion || base,
        _orgCreateSlugChecking: false,
        _orgCreateSlugStatus: suggestion ? 'suggested' : 'taken',
        _orgCreateSlugSuggestion: suggestion
      })
    } else {
      // Network error — allow user to proceed; server is the final arbiter.
      root.update({ _orgCreateSlugChecking: false, _orgCreateSlugStatus: 'error' })
    }
  }, 350)
}

export const NoOrgOverlay = {
  hide: (el, s) => {
    if (!USE_ONBOARDING_GATE) return true
    if (!s.root?.signedIn) return true
    return _hasTeamOrg(s.root)
  },

  position: 'fixed',
  inset: '0',
  zIndex: '500',
  background: 'black.85',
  backdropFilter: 'blur(12px)',
  align: 'center center',
  flow: 'column',

  Card: {
    flow: 'y',
    align: 'center center',
    gap: 'A',
    padding: 'C',
    maxWidth: 'H',
    round: 'B',
    background: 'overlayStrong',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    textAlign: 'center',
    boxShadow: '0 16px 40px var(--color-shadow)',

    Icon: { icon: 'people', boxSize: 'B1', color: 'caption' },

    Title: {
      tag: 'h2',
      fontSize: 'A2',
      fontWeight: '700',
      color: 'title',
      margin: '0',
      text: 'Create a team'
    },

    Body: {
      tag: 'p',
      fontSize: 'Z',
      color: 'caption',
      lineHeight: '1.55',
      margin: '0',
      maxWidth: 'G',
      text: 'Your workspace is a shared space for a team. Name your team to get started — you can invite teammates right after.'
    },

    Form: {
      tag: 'form',
      flow: 'y',
      gap: 'Z1',
      width: '100%',
      margin: 'A 0 0',
      textAlign: 'left',

      onSubmit: async (e, el, s) => {
        e.preventDefault()
        if (s.root?._orgCreateBusy) return
        const name = String(s.root?._orgCreateName || '').trim()
        if (!name) {
          s.root.update({ _orgCreateError: 'Name is required' })
          return
        }
        if (!getSymbolsToken()) {
          s.root.update({ _orgCreateError: 'Session expired — sign in again' })
          return
        }
        // Use the resolved (possibly suffixed) slug from live check; fall back
        // to slugifying the name if the live check never ran.
        const slug = s.root?._orgCreateSlug || _slugify(name) || undefined
        s.root.update({ _orgCreateBusy: true, _orgCreateError: null, _orgCreateSlugConflict: null })
        try {
          await getSDK().createOrganization({ name, slug, type: 'team' })
          // Pull the refreshed claims so the new org shows up in
          // root.jwtClaims.orgs. The overlay's `hide` predicate will
          // then return true and the workspace renders scoped to the
          // new org's home workspace.
          await refreshClaims()
          // Hard reload so every `method: 'select'` state query
          // re-runs under the new active_workspace_id.
          if (typeof window !== 'undefined') window.location.href = '/'
        } catch (err) {
          // Detect slug_taken specifically and offer a one-click fix.
          // The SDK throws with the server's error message; we sniff for the
          // 409/slug_taken signature in the message.
          const msg = err?.message || ''
          if (/409|slug_taken/iu.test(msg)) {
            // Race condition: slug was available at check time but taken by
            // the time we POSTed. Find the next free suffix and surface it.
            const base = _slugify(name)
            const suggestion = await _findAvailableSlug(base)
            s.root.update({
              _orgCreateBusy: false,
              _orgCreateError: `Slug "${slug}" was just taken.`,
              _orgCreateSlugConflict: slug,
              _orgCreateSlug: suggestion || slug,
              _orgCreateSlugSuggestion: suggestion,
              _orgCreateSlugStatus: suggestion ? 'suggested' : 'taken'
            })
            return
          }
          s.root.update({
            _orgCreateBusy: false,
            _orgCreateError: err?.message || 'Network error'
          })
        }
      },

      NameLabel: {
        tag: 'label',
        fontSize: 'Y2',
        fontWeight: '600',
        color: 'caption',
        text: 'Team name'
      },

      NameField: {
        tag: 'input',
        type: 'text',
        name: 'name',
        placeholder: 'e.g. Acme Co',
        required: true,
        maxlength: 60,
        padding: 'Z1 A',
        round: 'Z',
        border: '1px solid var(--color-line)',
        background: 'overlay',
        color: 'title',
        fontSize: 'Z1',
        width: '100%',
        onInput: (e, el, s) => {
          const val = e.target.value
          s.root.update({ _orgCreateName: val, _orgCreateError: null, _orgCreateSlugConflict: null })
          // Trigger debounced availability check; also resets user-edited slug.
          s.root.update({ _orgCreateSlugEdited: false })
          _scheduleSlugCheck(val, s.root)
        }
      },

      SlugLabel: {
        tag: 'label',
        fontSize: 'Y2',
        fontWeight: '600',
        color: 'caption',
        margin: 'Z 0 0',
        text: 'Slug (URL identifier)'
      },

      SlugRow: {
        flow: 'x',
        align: 'center left',
        gap: 'Z',
        width: '100%',

        SlugField: {
          tag: 'input',
          type: 'text',
          name: 'slug',
          placeholder: 'auto-derived from name',
          maxlength: 60,
          padding: 'Z1 A',
          round: 'Z',
          border: (el, s) => {
            const status = s.root?._orgCreateSlugStatus
            if (status === 'taken') return '1px solid var(--color-red)'
            if (status === 'available') return '1px solid var(--color-green)'
            return '1px solid var(--color-line)'
          },
          background: 'overlay',
          color: 'title',
          fontSize: 'Z1',
          flex: '1',
          // Keep the displayed value in sync with root._orgCreateSlug unless
          // the user has manually edited the field.
          onRender: (el, s) => {
            if (s.root?._orgCreateSlugEdited) return
            const slug = s.root?._orgCreateSlug || ''
            if (el.node && el.node.value !== slug) el.node.value = slug
          },
          onInput: (e, el, s) => {
            const val = _slugify(e.target.value)
            // Reflect normalized value back into the input.
            e.target.value = val
            s.root.update({
              _orgCreateSlug: val,
              _orgCreateSlugEdited: true,
              _orgCreateError: null,
              _orgCreateSlugConflict: null
            })
            _scheduleSlugCheck(val, s.root)
          }
        },

        SlugStatus: {
          tag: 'span',
          fontSize: 'Y2',
          whiteSpace: 'nowrap',
          hide: (el, s) => !s.root?._orgCreateSlug,
          color: (el, s) => {
            const status = s.root?._orgCreateSlugStatus
            if (s.root?._orgCreateSlugChecking) return 'caption'
            if (status === 'available') return 'green'
            if (status === 'taken') return 'red'
            if (status === 'suggested') return 'yellow'
            return 'caption'
          },
          text: (el, s) => {
            if (s.root?._orgCreateSlugChecking) return 'checking…'
            const status = s.root?._orgCreateSlugStatus
            if (status === 'available') return 'available'
            if (status === 'taken') return 'taken'
            if (status === 'suggested') return 'auto-adjusted'
            if (status === 'error') return ''
            return ''
          }
        }
      },

      // Shown when the live check found the slug taken and picked a suffix.
      // Also shown after a 409 slug_taken POST response with a one-click fix.
      SlugHint: {
        hide: (el, s) => {
          const status = s.root?._orgCreateSlugStatus
          const conflict = s.root?._orgCreateSlugConflict
          return status !== 'suggested' && status !== 'taken' && !conflict
        },
        flow: 'x',
        align: 'center left',
        gap: 'Z',
        margin: 'V 0 0',

        HintText: {
          tag: 'span',
          fontSize: 'Y2',
          color: 'caption',
          flex: '1',
          text: (el, s) => {
            const suggestion = s.root?._orgCreateSlugSuggestion
            const conflict = s.root?._orgCreateSlugConflict
            if (conflict && suggestion) {
              return `"${conflict}" was just taken — try "${suggestion}" instead.`
            }
            if (conflict) {
              return `"${conflict}" is taken. Edit the slug above to pick a different one.`
            }
            if (suggestion) {
              return `Slug auto-adjusted to "${suggestion}" — available and ready.`
            }
            return 'This slug is taken. Edit it above.'
          }
        },

        // One-click fix: apply the suggestion directly.
        UseSlugBtn: {
          tag: 'button',
          type: 'button',
          hide: (el, s) => !s.root?._orgCreateSlugSuggestion,
          padding: 'V Z1',
          round: 'Y',
          background: 'overlay',
          color: 'title',
          fontWeight: '600',
          fontSize: 'Y2',
          border: '1px solid var(--color-line)',
          cursor: 'pointer',
          ':hover': { background: 'overlayStrong' },
          text: (el, s) => {
            const sug = s.root?._orgCreateSlugSuggestion
            return sug ? `Use "${sug}"` : ''
          },
          onClick: (e, el, s) => {
            e.preventDefault()
            const sug = s.root?._orgCreateSlugSuggestion
            if (!sug) return
            s.root.update({
              _orgCreateSlug: sug,
              _orgCreateSlugEdited: true,
              _orgCreateSlugStatus: 'available',
              _orgCreateSlugSuggestion: null,
              _orgCreateSlugConflict: null,
              _orgCreateError: null
            })
            // Reflect in DOM input.
            const slugInput = el.getRoot()?.node?.querySelector('input[name="slug"]')
            if (slugInput) slugInput.value = sug
          }
        }
      },

      Error: {
        hide: (el, s) => !s.root?._orgCreateError,
        tag: 'p',
        color: 'red',
        fontSize: 'Y2',
        margin: 'Z 0 0',
        text: (el, s) => s.root?._orgCreateError || ''
      },

      Actions: {
        flow: 'x',
        gap: 'Z1',
        margin: 'A 0 0',
        justifyContent: 'center',

        Submit: {
          tag: 'button',
          type: 'submit',
          disabled: (el, s) =>
            !!s.root?._orgCreateBusy || s.root?._orgCreateSlugChecking === true,
          padding: 'Z1 B',
          round: 'Z',
          background: 'blue',
          color: 'white',
          fontWeight: '600',
          fontSize: 'Z1',
          border: 'none',
          cursor: 'pointer',
          ':hover': { filter: 'brightness(1.1)' },
          ':disabled': { opacity: '.6', cursor: 'wait' },
          text: (el, s) =>
            s.root?._orgCreateBusy ? 'Creating…' : 'Create organization'
        },

        SignOutLink: {
          extends: 'Link',
          href: '/login',
          padding: 'Z1 B',
          round: 'Z',
          background: 'transparent',
          color: 'caption',
          fontWeight: '500',
          fontSize: 'Z1',
          textDecoration: 'none',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',
          ':hover': { background: 'overlay', color: 'title' },
          // migrated 2026-04-27 — flat sdk.logout() replaces sb.auth.signOut.
          onClick: (e, el, s) => {
            el.sdk?.logout?.().catch(() => {})
          },
          text: 'Sign out'
        }
      }
    }
  }
}
